import { MicrophoneIcon } from '@daily/shared/components/Icons/MicrophoneIcon';
import { RaiseHandIcon } from '@daily/shared/components/Icons/RaiseHandIcon';
import { WarningIcon } from '@daily/shared/components/Icons/WarningIcon';
import { Text } from '@daily/shared/components/Text';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useNetwork, useParticipantProperty } from '@daily-co/daily-react';
import classnames from 'classnames';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCallState } from '/contexts/CallProvider';
import { useHandRaisedQueue } from '/hooks/useHandRaising';
import { isSafari } from '/lib/browserConfig';
import { isTrackOff } from '/lib/participants';

import { MicMeter } from './MicMeter';

interface Props {
  activeIndicator?: 'meter' | 'static';
  isLocal?: boolean;
  name?: string;
  sessionId: string;
}

export const TileInfo: React.FC<React.PropsWithChildren<Props>> = memo(
  ({ activeIndicator = 'static', isLocal = false, name, sessionId }) => {
    const { t } = useTranslation();
    const { disableAudio } = useCallState();
    const { colors, isDarkMode } = useTheme();
    const audioTrackState = useParticipantProperty(
      sessionId,
      'tracks.audio.state'
    );
    const { threshold } = useNetwork();
    const handRaisedQueueNumber = useHandRaisedQueue(sessionId);

    const isSafari15OrLower = isSafari(1, 15);

    const hasNetworkIssues = isLocal && ['low', 'very-low'].includes(threshold);

    const NameText = useMemo(() => {
      if (!name) return null;
      let color: React.ComponentProps<typeof Text>['color'] = 'white';
      let text = name;
      if (hasNetworkIssues) {
        switch (threshold) {
          case 'low':
            color = 'warning';
            text = t('network.weakNetwork');
            break;
          case 'very-low':
            color = 'error';
            text = t('network.poorConnection');
            break;
        }
      }
      return (
        <Text className="name" El="div" color={color} variant="strong" truncate>
          {text}
        </Text>
      );
    }, [hasNetworkIssues, name, t, threshold]);

    return (
      <div className="tile-info">
        {!!handRaisedQueueNumber && (
          <div
            className={classnames('hand-status', {
              'hand-status-local': isLocal,
            })}
          >
            <RaiseHandIcon size={24} />
            <span className="queue-number">{handRaisedQueueNumber}</span>
          </div>
        )}
        <div className="info">
          {(hasNetworkIssues || !disableAudio) && (
            <div
              className={classnames('mic', {
                muted: isTrackOff(audioTrackState),
              })}
            >
              {hasNetworkIssues && (
                <WarningIcon
                  color={
                    threshold === 'low'
                      ? colors.system.orange
                      : colors.system.red
                  }
                  size={16}
                />
              )}
              {!hasNetworkIssues && !disableAudio && (
                <>
                  {activeIndicator === 'meter' && !isSafari15OrLower && (
                    <MicMeter sessionId={sessionId} />
                  )}
                  <MicrophoneIcon
                    color={colors.system.white}
                    muted={isTrackOff(audioTrackState)}
                    size={16}
                  />
                </>
              )}
            </div>
          )}
          {NameText}
        </div>
        <style jsx>{`
          .tile-info {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }
          .hand-status {
            align-items: center;
            background: ${colors.background};
            border-radius: 8px;
            border: 1px solid ${colors.border};
            box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
            color: ${colors.baseText};
            display: flex;
            gap: 8px;
            left: 0;
            margin: 8px;
            padding: 8px;
            position: absolute;
            top: 0;
          }
          .hand-status-local {
            --blend: ${isDarkMode
              ? 'rgba(0, 0, 0, 0.3)'
              : 'rgba(255, 255, 255, 0.7)'};

            background: linear-gradient(0deg, var(--blend), var(--blend)),
              ${colors.accent};
            border-color: ${colors.accent};
            color: ${colors.accentText};
          }
          .queue-number {
            font-size: 16px;
            font-variant-numeric: tabular-nums;
            font-weight: 600;
            text-align: center;
          }
          .info {
            align-items: stretch;
            background: rgba(0, 0, 0, 0.6);
            bottom: 0;
            color: var(--text-default);
            display: flex;
            flex-direction: row;
            justify-content: stretch;
            left: 0;
            max-width: 100%;
            position: absolute;
          }
          .info .mic {
            margin-right: 1px;
            min-width: 24px;
          }
          .info .mic,
          .info :global(.name) {
            padding: 4px;
          }
          .info :global(.name) {
            height: 100%;
          }
          .mic :global(svg) {
            position: relative;
            z-index: 2;
          }
        `}</style>
      </div>
    );
  }
);
TileInfo.displayName = 'TileInfo';
